// Apple-style in-app notifications — slide-in toasts + bell + panel.
//
// One client instance per portal. Construct via createNotificationsClient(...),
// then:
//   • call .start() once the user is authenticated
//   • mount the bell+panel via .attachBell({ container, onNavigate })
//   • call .stop() on logout / shell unmount
//
// Backed by /api/portal/notifications + /notification-preferences (PG-backed,
// RLS-scoped). Polls every 30 s by default, pauses while the tab is hidden,
// resumes on focus.
//
// No emoji anywhere — every glyph routes through renderIcon() from
// @tkos/shared-ui icons. Colours come from tokens.css.

import './notifications.css';
import { renderIcon, type IconName } from '../../icons';

export type NotificationType =
  | 'submission_received'
  | 'message_received'
  | 'quote_viewed'
  | 'quote_signed'
  | 'payment_received'
  | 'delivery_status_changed'
  | 'booking_requested'
  | 'snag_raised'
  | 'supplier_deal_posted'
  | 'lead_scored_hot'
  | 'layout_needs_review';

export const NOTIFICATION_TYPES: readonly NotificationType[] = [
  'submission_received',
  'message_received',
  'quote_viewed',
  'quote_signed',
  'payment_received',
  'delivery_status_changed',
  'booking_requested',
  'snag_raised',
  'supplier_deal_posted',
  'lead_scored_hot',
  'layout_needs_review',
];

/** Human label per type — used in the settings preferences UI + a11y. */
export const NOTIFICATION_LABELS: Readonly<Record<NotificationType, string>> = {
  submission_received:     'New lead submissions',
  message_received:        'New messages',
  quote_viewed:            'Quote viewed by customer',
  quote_signed:            'Quote signed',
  payment_received:        'Payment received',
  delivery_status_changed: 'Delivery status changed',
  booking_requested:       'Booking requested',
  snag_raised:             'Snag raised',
  supplier_deal_posted:    'Supplier deal posted',
  lead_scored_hot:         'Lead scored hot',
  layout_needs_review:     'AI layout needs review',
};

export interface NotificationItem {
  id: string;
  type: NotificationType;
  title: string;
  body?: string;
  customerName?: string;
  customerId?: string;
  linkPath?: string;
  iconName?: string;
  createdAt: number;
  read: boolean;
}

export interface NotificationsClient {
  /** Open the bell+panel in the given DOM container. Returns a handle. */
  attachBell(opts: { container: HTMLElement; onNavigate: (path: string, item?: NotificationItem) => void }): { destroy: () => void };
  /** Start polling for new notifications. */
  start(): void;
  /** Stop polling and clear visibility listener. */
  stop(): void;
  /** Fetch the list immediately (e.g. on a realtime event) without waiting
   *  for the next poll tick. No-op if a fetch is already in flight. */
  refresh(): void;
  /** Get the current unread count synchronously. */
  unreadCount(): number;
  /** Get the current list synchronously. */
  list(): readonly NotificationItem[];
  /** Subscribe to change events. */
  on(event: 'change', fn: () => void): () => void;
}

interface ApiFn {
  (path: string, init?: RequestInit): Promise<unknown>;
}

interface ListResponse {
  ok?: boolean;
  notifications?: unknown[];
  unreadCount?: number;
}

/** Default icon when the server didn't suggest one and the type isn't mapped. */
const FALLBACK_ICON: IconName = 'bell';

/** Per-type icon fallback (kept in sync with backend producer suggestions). */
const TYPE_ICON: Readonly<Record<NotificationType, IconName>> = {
  submission_received:     'file',
  message_received:        'chat',
  quote_viewed:            'eye',
  quote_signed:            'signature',
  payment_received:        'pound',
  delivery_status_changed: 'truck',
  booking_requested:       'calendar',
  snag_raised:             'alert-triangle',
  supplier_deal_posted:    'tag',
  lead_scored_hot:         'sparkle',
  layout_needs_review:     'alert-triangle',
};

function pickIcon(item: NotificationItem): IconName {
  const known = new Set<string>(Object.keys(TYPE_ICON));
  if (item.iconName && /^[a-z][a-z0-9-]+$/i.test(item.iconName)) {
    return item.iconName as IconName;
  }
  if (known.has(item.type)) return TYPE_ICON[item.type];
  return FALLBACK_ICON;
}

function coerceItem(raw: unknown): NotificationItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = typeof r['id'] === 'string' ? r['id'] : null;
  const type = typeof r['type'] === 'string' ? r['type'] : null;
  const title = typeof r['title'] === 'string' ? r['title'] : null;
  if (!id || !type || !title) return null;
  const createdAt = typeof r['createdAt'] === 'number' ? r['createdAt'] : 0;
  const out: NotificationItem = {
    id,
    type: type as NotificationType,
    title,
    createdAt,
    read: Boolean(r['read']),
  };
  if (typeof r['body'] === 'string') out.body = r['body'];
  if (typeof r['customerName'] === 'string') out.customerName = r['customerName'];
  if (typeof r['customerId'] === 'string') out.customerId = r['customerId'];
  if (typeof r['linkPath'] === 'string') out.linkPath = r['linkPath'];
  if (typeof r['iconName'] === 'string') out.iconName = r['iconName'];
  return out;
}

function formatAgo(ms: number): string {
  if (!ms) return '';
  const diff = Date.now() - ms;
  if (diff < 0) return 'just now';
  const s = Math.floor(diff / 1000);
  if (s < 45) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} day${d === 1 ? '' : 's'} ago`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w} wk${w === 1 ? '' : 's'} ago`;
  return new Date(ms).toLocaleDateString();
}

export interface CreateNotificationsClientOpts {
  api: ApiFn;
  pollIntervalMs?: number;
}

export function createNotificationsClient(opts: CreateNotificationsClientOpts): NotificationsClient {
  const api = opts.api;
  const pollMs = Math.max(5000, opts.pollIntervalMs ?? 30_000);

  // ── State ─────────────────────────────────────────────────────────
  let items: NotificationItem[] = [];
  let seenIds = new Set<string>(); // ids we've already shown a slide-in for
  let firstFetchDone = false;
  let timer: ReturnType<typeof setInterval> | null = null;
  let running = false;
  let onNavigateCb: ((path: string, item?: NotificationItem) => void) | null = null;

  const changeListeners = new Set<() => void>();
  const fireChange = (): void => {
    for (const fn of changeListeners) {
      try { fn(); } catch (err) { console.warn('[notifications] listener threw', err); }
    }
  };

  // ── Slide-in queue (one toast at a time) ──────────────────────────
  type QueueEntry = { item: NotificationItem };
  const queue: QueueEntry[] = [];
  let activeToast: HTMLElement | null = null;
  let activeTimer: ReturnType<typeof setTimeout> | null = null;

  const ensureToastRegion = (): HTMLElement => {
    let region = document.getElementById('tkos-notif-region');
    if (region) return region;
    region = document.createElement('div');
    region.id = 'tkos-notif-region';
    region.className = 'tkos-notif-region';
    region.setAttribute('role', 'region');
    region.setAttribute('aria-label', 'Notifications');
    document.body.appendChild(region);
    return region;
  };

  const dismissToast = (): void => {
    if (!activeToast) return;
    const el = activeToast;
    activeToast = null;
    if (activeTimer !== null) {
      clearTimeout(activeTimer);
      activeTimer = null;
    }
    el.classList.add('tkos-notif-toast--leaving');
    const finish = (): void => {
      el.remove();
      // Process next in queue.
      window.setTimeout(processQueue, 80);
    };
    let done = false;
    const handler = (): void => {
      if (done) return;
      done = true;
      el.removeEventListener('animationend', handler);
      finish();
    };
    el.addEventListener('animationend', handler);
    window.setTimeout(handler, 350);
  };

  const showToast = (item: NotificationItem): void => {
    const region = ensureToastRegion();
    const el = document.createElement('div');
    el.className = 'tkos-notif-toast';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.dataset['notifId'] = item.id;

    // Icon
    const iconWrap = document.createElement('span');
    iconWrap.className = 'tkos-notif-toast__icon';
    iconWrap.appendChild(renderIcon(pickIcon(item), { size: 'md' }));
    el.appendChild(iconWrap);

    // Body
    const body = document.createElement('div');
    body.className = 'tkos-notif-toast__body';
    const title = document.createElement('div');
    title.className = 'tkos-notif-toast__title';
    title.textContent = item.title;
    body.appendChild(title);
    if (item.body) {
      const desc = document.createElement('div');
      desc.className = 'tkos-notif-toast__desc';
      desc.textContent = item.body;
      body.appendChild(desc);
    }
    if (item.customerName && !item.title.includes(item.customerName)) {
      const meta = document.createElement('div');
      meta.className = 'tkos-notif-toast__meta';
      meta.textContent = item.customerName;
      body.appendChild(meta);
    }
    el.appendChild(body);

    // Close button
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'tkos-notif-toast__close';
    close.setAttribute('aria-label', 'Dismiss notification');
    close.appendChild(renderIcon('close', { size: 'sm' }));
    close.addEventListener('click', (e) => {
      e.stopPropagation();
      dismissToast();
    });
    el.appendChild(close);

    // Click → navigate + mark read
    el.addEventListener('click', () => {
      void markRead(item.id);
      if (item.linkPath && onNavigateCb) onNavigateCb(item.linkPath, item);
      dismissToast();
    });

    // Hover pauses the dismiss timer
    el.addEventListener('mouseenter', () => {
      if (activeTimer !== null) {
        clearTimeout(activeTimer);
        activeTimer = null;
      }
    });
    el.addEventListener('mouseleave', () => {
      if (activeToast === el && activeTimer === null) {
        activeTimer = setTimeout(dismissToast, 2000);
      }
    });

    region.appendChild(el);
    activeToast = el;
    activeTimer = setTimeout(dismissToast, 5000);
  };

  const processQueue = (): void => {
    if (activeToast) return;
    const next = queue.shift();
    if (!next) return;
    showToast(next.item);
  };

  const enqueueToast = (item: NotificationItem): void => {
    queue.push({ item });
    processQueue();
  };

  // ── API helpers ───────────────────────────────────────────────────
  const fetchList = async (): Promise<void> => {
    let resp: ListResponse;
    try {
      resp = (await api('/portal/notifications?limit=50')) as ListResponse;
    } catch (err) {
      // Silent failure — polling will retry. Don't spam console.
      void err;
      return;
    }
    if (!resp || !Array.isArray(resp.notifications)) return;
    const next = resp.notifications
      .map(coerceItem)
      .filter((x): x is NotificationItem => x !== null);

    // Detect brand-new unread items to slide-in. Only after the first
    // fetch — on initial load we just seed `seenIds` so old unread
    // notifications don't all slide in at once when the user opens
    // the portal.
    if (firstFetchDone) {
      const knownIds = new Set(items.map((x) => x.id));
      for (const it of next) {
        if (!it.read && !knownIds.has(it.id) && !seenIds.has(it.id)) {
          enqueueToast(it);
          seenIds.add(it.id);
        }
      }
    } else {
      // Seed seenIds with everything currently present so existing
      // unread items don't fire on first poll.
      for (const it of next) seenIds.add(it.id);
      firstFetchDone = true;
    }

    items = next;
    fireChange();
  };

  const markRead = async (id: string): Promise<void> => {
    // Optimistic update
    items = items.map((x) => (x.id === id && !x.read ? { ...x, read: true } : x));
    fireChange();
    try {
      await api(`/portal/notifications/${encodeURIComponent(id)}/read`, { method: 'POST' });
    } catch (err) {
      void err;
    }
  };

  const markAllRead = async (): Promise<void> => {
    items = items.map((x) => (x.read ? x : { ...x, read: true }));
    fireChange();
    try {
      await api('/portal/notifications/mark-all-read', { method: 'POST' });
    } catch (err) {
      void err;
    }
  };

  const clearAll = async (): Promise<void> => {
    items = [];
    fireChange();
    try {
      await api('/portal/notifications', { method: 'DELETE' });
    } catch (err) {
      void err;
    }
  };

  const deleteOne = async (id: string): Promise<void> => {
    items = items.filter((x) => x.id !== id);
    fireChange();
    try {
      await api(`/portal/notifications/${encodeURIComponent(id)}`, { method: 'DELETE' });
    } catch (err) {
      void err;
    }
  };

  // ── Polling lifecycle ─────────────────────────────────────────────
  const startTimer = (): void => {
    if (timer !== null) return;
    timer = setInterval(() => { void fetchList(); }, pollMs);
  };
  const stopTimer = (): void => {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };

  const onVisibility = (): void => {
    if (!running) return;
    if (document.hidden) {
      stopTimer();
    } else {
      // Resume — fire one fetch immediately, then restart interval.
      void fetchList();
      startTimer();
    }
  };

  // ── Public surface ────────────────────────────────────────────────
  const client: NotificationsClient = {
    start: () => {
      if (running) return;
      running = true;
      document.addEventListener('visibilitychange', onVisibility);
      void fetchList();
      startTimer();
    },
    stop: () => {
      running = false;
      stopTimer();
      document.removeEventListener('visibilitychange', onVisibility);
    },
    refresh: () => { if (running) void fetchList(); },
    unreadCount: () => items.reduce((n, x) => n + (x.read ? 0 : 1), 0),
    list: () => items,
    on: (_event, fn) => {
      changeListeners.add(fn);
      return () => changeListeners.delete(fn);
    },
    attachBell: ({ container, onNavigate }) => {
      onNavigateCb = onNavigate;

      // Build button
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'tkos-notif-bell';
      btn.setAttribute('aria-label', 'Notifications');
      btn.title = 'Notifications';
      btn.appendChild(renderIcon('bell', { size: 'md' }));

      const badge = document.createElement('span');
      badge.className = 'tkos-notif-bell__badge';
      badge.setAttribute('aria-hidden', 'true');
      btn.appendChild(badge);

      container.appendChild(btn);

      // Panel
      let panel: HTMLElement | null = null;
      let backdrop: HTMLElement | null = null;
      let escHandler: ((e: KeyboardEvent) => void) | null = null;
      let panelOffChange: (() => void) | null = null;

      const closePanel = (): void => {
        if (!panel) return;
        panel.classList.add('tkos-notif-panel--leaving');
        const p = panel;
        const b = backdrop;
        panel = null;
        backdrop = null;
        if (escHandler) {
          document.removeEventListener('keydown', escHandler);
          escHandler = null;
        }
        if (panelOffChange) {
          panelOffChange();
          panelOffChange = null;
        }
        window.setTimeout(() => {
          p.remove();
          if (b) b.remove();
        }, 220);
      };

      const renderList = (listEl: HTMLElement): void => {
        listEl.replaceChildren();
        if (items.length === 0) {
          const empty = document.createElement('div');
          empty.className = 'tkos-notif-panel__empty';
          const icon = document.createElement('span');
          icon.className = 'tkos-notif-panel__empty-icon';
          icon.appendChild(renderIcon('bell', { size: 'lg' }));
          empty.appendChild(icon);
          const msg = document.createElement('div');
          msg.className = 'tkos-notif-panel__empty-msg';
          msg.textContent = "You're all caught up.";
          empty.appendChild(msg);
          listEl.appendChild(empty);
          return;
        }
        for (const it of items) {
          const row = document.createElement('div');
          row.className = 'tkos-notif-row' + (it.read ? '' : ' tkos-notif-row--unread');
          row.setAttribute('role', 'button');
          row.tabIndex = 0;
          row.dataset['notifId'] = it.id;

          const iconWrap = document.createElement('span');
          iconWrap.className = 'tkos-notif-row__icon';
          iconWrap.appendChild(renderIcon(pickIcon(it), { size: 'sm' }));
          row.appendChild(iconWrap);

          const main = document.createElement('div');
          main.className = 'tkos-notif-row__main';
          const title = document.createElement('div');
          title.className = 'tkos-notif-row__title';
          title.textContent = it.title;
          main.appendChild(title);
          if (it.body) {
            const body = document.createElement('div');
            body.className = 'tkos-notif-row__body';
            body.textContent = it.body;
            main.appendChild(body);
          }
          const ago = document.createElement('div');
          ago.className = 'tkos-notif-row__ago';
          ago.textContent = formatAgo(it.createdAt);
          main.appendChild(ago);
          row.appendChild(main);

          const del = document.createElement('button');
          del.type = 'button';
          del.className = 'tkos-notif-row__del';
          del.setAttribute('aria-label', 'Remove notification');
          del.appendChild(renderIcon('close', { size: 'xs' }));
          del.addEventListener('click', (e) => {
            e.stopPropagation();
            void deleteOne(it.id);
          });
          row.appendChild(del);

          const activate = (): void => {
            void markRead(it.id);
            if (it.linkPath && onNavigateCb) onNavigateCb(it.linkPath, it);
            closePanel();
          };
          row.addEventListener('click', activate);
          row.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              activate();
            }
          });

          listEl.appendChild(row);
        }
      };

      const openPanel = (): void => {
        if (panel) {
          closePanel();
          return;
        }
        backdrop = document.createElement('div');
        backdrop.className = 'tkos-notif-panel-backdrop';
        backdrop.addEventListener('click', closePanel);
        document.body.appendChild(backdrop);

        panel = document.createElement('div');
        panel.className = 'tkos-notif-panel';
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-label', 'Notifications');

        const header = document.createElement('div');
        header.className = 'tkos-notif-panel__header';
        const titleEl = document.createElement('div');
        titleEl.className = 'tkos-notif-panel__title';
        titleEl.textContent = 'Notifications';
        header.appendChild(titleEl);

        const actions = document.createElement('div');
        actions.className = 'tkos-notif-panel__actions';

        const markAllBtn = document.createElement('button');
        markAllBtn.type = 'button';
        markAllBtn.className = 'tkos-notif-panel__action';
        markAllBtn.textContent = 'Mark all as read';
        markAllBtn.addEventListener('click', () => { void markAllRead(); });
        actions.appendChild(markAllBtn);

        const clearBtn = document.createElement('button');
        clearBtn.type = 'button';
        clearBtn.className = 'tkos-notif-panel__action';
        clearBtn.textContent = 'Clear all';
        clearBtn.addEventListener('click', () => { void clearAll(); });
        actions.appendChild(clearBtn);

        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.className = 'tkos-notif-panel__close';
        closeBtn.setAttribute('aria-label', 'Close notifications');
        closeBtn.appendChild(renderIcon('close', { size: 'sm' }));
        closeBtn.addEventListener('click', closePanel);
        actions.appendChild(closeBtn);

        header.appendChild(actions);
        panel.appendChild(header);

        const listEl = document.createElement('div');
        listEl.className = 'tkos-notif-panel__list';
        renderList(listEl);
        panel.appendChild(listEl);

        document.body.appendChild(panel);
        // animate-in next frame
        window.requestAnimationFrame(() => panel?.classList.add('tkos-notif-panel--open'));

        escHandler = (e: KeyboardEvent): void => {
          if (e.key === 'Escape') closePanel();
        };
        document.addEventListener('keydown', escHandler);

        // Re-render the panel list whenever our state changes.
        panelOffChange = client.on('change', () => {
          renderList(listEl);
        });
      };

      btn.addEventListener('click', openPanel);

      // Render badge based on state
      const renderBadge = (): void => {
        const n = client.unreadCount();
        if (n <= 0) {
          badge.textContent = '';
          badge.dataset['empty'] = 'true';
          btn.classList.remove('tkos-notif-bell--has-unread');
        } else {
          badge.textContent = n > 99 ? '99+' : String(n);
          delete badge.dataset['empty'];
          btn.classList.add('tkos-notif-bell--has-unread');
        }
      };
      renderBadge();
      const offChange = client.on('change', renderBadge);

      return {
        destroy: () => {
          offChange();
          if (panel) closePanel();
          btn.remove();
        },
      };
    },
  };

  return client;
}
